/**
 * Helper functions to categorize objects as fixtures or furnishings
 */

import { isSink } from '@shared-lib/sink-types';
import { getObjectType } from './objectDrawingUtils';
import { isBathroomFixtureType } from '../constants/bathroomFixtureConstants';
import { isElectricalDeviceType } from './electricalDevices';
import { COUNTERTOP_PARENT_TYPES } from '@shared-lib/cabinet-runs/countertopEnds';

/**
 * Object types that can have a countertop placed on top
 * Used for smart placement detection and parent-child relationships
 */
// One list, shared with the estimate (`countertopSupportCabinet`).
export { COUNTERTOP_PARENT_TYPES };

/**
 * Check if an object type can have a countertop placed on top
 * @param {string} objectType - The type of object
 * @returns {boolean} - True if countertops can be placed on this object type
 */
export const canHaveCountertopOnTop = (objectType) => {
  if (!objectType) return false;
  return COUNTERTOP_PARENT_TYPES.includes(objectType.toLowerCase());
};

/**
 * Check if an object can have a countertop placed on top
 * @param {Object} obj - The object to check
 * @returns {boolean} - True if countertops can be placed on this object
 */
export const objectCanHaveCountertop = (obj) => {
  if (!obj) return false;

  const type = obj.type?.toLowerCase() || '';
  if (COUNTERTOP_PARENT_TYPES.includes(type)) return true;

  // Check category object
  if (typeof obj.category === 'object' && obj.category !== null) {
    return COUNTERTOP_PARENT_TYPES.some(
      (parentType) => obj.category[parentType]
    );
  }

  return false;
};

/**
 * Determines if an object is electrical (lights, switches, outlets).
 * This is the single source of truth for the "Electrical" visibility layer.
 * Lights are electrical-only (no longer fixtures). Switches are classified
 * by type/category inline (mirrors the canonical isSwitch() logic in
 * electricalSwitchUtils.js — not imported here to avoid pulling
 * lightingManager → three into this leaf utility). Outlets are included for
 * forward compatibility — nothing renders them yet, so toggling them is a
 * no-op today.
 * @param {Object} obj - The object to categorize
 * @returns {boolean} - True if object is an electrical element
 */
export const isElectrical = (obj) => {
  if (!obj) return false;

  const type = obj.type?.toLowerCase() || '';

  // Switches, outlets and ceiling devices from the Electrical tray catalog.
  if (isElectricalDeviceType(type)) {
    return true;
  }

  // Lights (ceiling / wall / recessed / lamps / sconces).
  if (type.includes('light') || type.includes('lamp') || type === 'sconce') {
    return true;
  }

  // Outlets / receptacles. (No rendering exists yet — see scope notes.)
  if (type.includes('outlet') || type.includes('receptacle')) {
    return true;
  }

  // Category field (string or object form).
  if (obj.category) {
    if (typeof obj.category === 'string') {
      const category = obj.category.toLowerCase();
      return (
        category.includes('lighting') ||
        category.includes('electrical') ||
        category.includes('ceiling-light') ||
        category.includes('wall-light') ||
        category.includes('sconce') ||
        category === '3way' ||
        category.includes('outlet') ||
        category.includes('receptacle') ||
        category.includes('switch') ||
        category.includes('dimmer')
      );
    } else if (typeof obj.category === 'object') {
      const categoryKeys = Object.keys(obj.category).map((k) =>
        k.toLowerCase()
      );
      return (
        categoryKeys.includes('lighting') ||
        categoryKeys.includes('ceiling-light') ||
        categoryKeys.includes('wall-light') ||
        categoryKeys.includes('sconce') ||
        categoryKeys.includes('outlet') ||
        categoryKeys.includes('receptacle') ||
        categoryKeys.includes('electrical') ||
        categoryKeys.includes('switch') ||
        categoryKeys.includes('dimmer') ||
        categoryKeys.includes('3way')
      );
    }
  }

  return false;
};

/**
 * Determines if an object is a fixture (built-in/permanent items)
 * Fixtures include: sinks, toilets, bathtubs, showers, stoves, ovens, dishwashers, etc.
 * @param {Object} obj - The object to categorize
 * @returns {boolean} - True if object is a fixture
 */
export const isFixture = (obj) => {
  if (!obj) return false;

  // Lights/switches/outlets are electrical-only — never fixtures.
  if (isElectrical(obj)) return false;

  // Sink check via canonical helper (covers all legacy types + new canonical shape)
  if (isSink(obj)) return true;

  // Check type field
  const type = obj.type?.toLowerCase() || '';
  const fixtureTypes = [
    'bathroom-sink-faucet', // Faucet mounted on sink
    'toilet',
    'bathtub',
    'shower',
    // Both old (stove / oven) and new (range / wall-oven) names — saved
    // structures from before the GLB-aligned type rename flow through
    // this filter too.
    'stove',
    'oven',
    'wall-oven',
    'range',
    'dishwasher',
    'refrigerator',
    'washer',
    'dryer',
    'water-heater',
    'furnace',
    'hvac',
    'radiator',
    'lower-cabinet', // Fixed cabinet mounted to floor
    'upper-cabinet', // Fixed cabinet mounted to wall
    'countertop', // Fixed countertop surface
    'shower-controls',
    'shower-stem',
  ];

  if (fixtureTypes.some((fixture) => type.includes(fixture))) {
    return true;
  }

  // Check category field
  if (obj.category) {
    // Category can be a string or object
    if (typeof obj.category === 'string') {
      const category = obj.category.toLowerCase();
      return (
        category.includes('plumbing') ||
        category.includes('appliance') ||
        category.includes('hvac') ||
        fixtureTypes.some((fixture) => category.includes(fixture))
      );
    } else if (typeof obj.category === 'object') {
      // Check if any fixture type is a key in the category object
      const categoryKeys = Object.keys(obj.category).map((k) =>
        k.toLowerCase()
      );
      return categoryKeys.some((key) => fixtureTypes.includes(key));
    }
  }

  return false;
};

/**
 * Canonical "fixed in place" predicate — the single source of truth for the
 * Fixtures visibility layer across all three viewer modes (3D / floorplan /
 * elevations). Electrical is excluded first (it is its own layer, routed before
 * this by the visibility router). Mirror is intentionally NOT a fixture.
 * @param {Object} obj - The object to categorize
 * @returns {boolean} - True if object is fixed-in-place (a fixture)
 */
export const isFixedInPlace = (obj) => {
  if (!obj) return false;
  if (isElectrical(obj)) return false; // electrical is its own layer, checked first by the router
  const type = getObjectType(obj).toLowerCase();
  // Plumbing / bathroom fixtures + sinks
  if (isBathroomFixtureType(type)) return true;
  if (isSink(obj)) return true;
  // Built-ins by substring
  if (type.includes('cabinet')) return true; // lower/upper/wine cabinet
  if (type.includes('counter')) return true; // counter + countertop
  if (type.includes('panel') || type.includes('surround')) return true;
  if (type.includes('faucet')) return true;
  if (type.includes('hood')) return true; // vent-hood / range-hood
  if (type.includes('shower')) return true;
  // Exact-match fixed-in-place: built-in clusters, major appliances, mechanicals
  const FIXED_TYPES = new Set([
    'bath',
    'island',
    'peninsula',
    'stove',
    'range',
    'oven',
    'wall-oven',
    'cooktop',
    'microwave',
    'refrigerator',
    'fridge',
    'freezer',
    'wine-cabinet',
    'warming-drawer',
    'outdoor-grill',
    'dishwasher',
    'washerdryer',
    'fireplace',
    'wood-stove',
    'water-heater',
    'furnace',
    'hvac',
    'radiator',
    'toilet-controller',
  ]);
  if (FIXED_TYPES.has(type)) return true;
  // category-based (mirror the category check in isFixture)
  if (obj.category) {
    if (typeof obj.category === 'string') {
      const c = obj.category.toLowerCase();
      if (
        c.includes('plumbing') ||
        c.includes('appliance') ||
        c.includes('hvac')
      )
        return true;
    } else if (typeof obj.category === 'object') {
      const keys = Object.keys(obj.category).map((k) => k.toLowerCase());
      if (
        keys.some(
          (k) =>
            k.includes('plumbing') ||
            k.includes('appliance') ||
            k.includes('hvac')
        )
      )
        return true;
    }
  }
  return false;
};

/**
 * Determines if an object is furnishing (moveable furniture/decor)
 * Furnishings include: chairs, tables, beds, sofas, desks, storage, etc.
 * @param {Object} obj - The object to categorize
 * @returns {boolean} - True if object is furnishing
 */
export const isFurnishing = (obj) => {
  if (!obj) return false;

  // Check type field
  const type = obj.type?.toLowerCase() || '';
  const furnishingTypes = [
    'chair',
    'table',
    'desk',
    'bed',
    'sofa',
    'couch',
    'dresser',
    'shelf',
    'shelves',
    'bookcase',
    'wardrobe',
    'nightstand',
    'ottoman',
    'bench',
    'lamp',
    'mirror',
    'rug',
    'plant',
    'artwork',
    'picture',
    'television',
    'monitor',
    'storage-furniture', // Movable storage units
    'storage', // Legacy storage items (treat as movable by default)
  ];

  if (furnishingTypes.some((furnishing) => type.includes(furnishing))) {
    return true;
  }

  // Check category field
  if (obj.category) {
    // Category can be a string or object
    if (typeof obj.category === 'string') {
      const category = obj.category.toLowerCase();
      return (
        category.includes('furniture') ||
        category.includes('decor') ||
        category.includes('seating') ||
        category.includes('storage') ||
        furnishingTypes.some((furnishing) => category.includes(furnishing))
      );
    } else if (typeof obj.category === 'object') {
      // Check for furniture or chair/table keys
      const categoryKeys = Object.keys(obj.category).map((k) =>
        k.toLowerCase()
      );
      return (
        categoryKeys.includes('furniture') ||
        categoryKeys.includes('chair') ||
        categoryKeys.includes('table') ||
        categoryKeys.some((key) => furnishingTypes.includes(key))
      );
    }
  }

  // If not explicitly a fixture and has a type/category, assume it's furnishing
  // This catches miscellaneous objects that aren't fixtures
  if (!isFixture(obj) && (obj.type || obj.category)) {
    return true;
  }

  return false;
};
